import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config';
import {
    Box,
    Button,
    Card,
    CardContent,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Grid,
    IconButton,
    Alert,
    Snackbar,
    CircularProgress,
    ImageList,
    ImageListItem
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import CreatePinModal from './CreatePinModal';
import ProductCard from './ProductCard';
import { styled } from '@mui/material/styles';

// Styled drop zone
const DropZone = styled(Box)(({ theme, isdragover }) => ({
    height: 180,
    background: '#eee',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: isdragover ? '2px dashed #1976d2' : '2px dashed #ccc',
    position: 'relative',
    overflowX: 'auto',
    padding: 8,
    transition: 'border 0.2s',
    cursor: 'pointer',
}));

const InspirationBoard = () => {
    const [boards, setBoards] = useState([]);
    const [savedItems, setSavedItems] = useState([]);
    const [selectedBoard, setSelectedBoard] = useState(null);
    const [openCreatePin, setOpenCreatePin] = useState(false);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const { isAuthenticated } = useAuth();
    // Add drag state
    const [dragOverBoardId, setDragOverBoardId] = useState(null);

    useEffect(() => {
        if (!isAuthenticated) {
            navigate('/login');
            return;
        }
        fetchBoardsAndSavedItems();
    }, [isAuthenticated, navigate]);

    const fetchBoardsAndSavedItems = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            if (!token) {
                throw new Error('No authentication token found');
            }
            const [boardsRes, savedRes] = await Promise.all([
                axios.get(`${API_URL}/api/boards`, {
                    headers: { 'x-auth-token': token }
                }),
                axios.get(`${API_URL}/api/user/saved`, {
                    headers: { 'x-auth-token': token }
                })
            ]);
            const boardsWithItems = boardsRes.data.map(board => ({
                ...board,
                items: board.items || []
            }));
            setBoards(boardsWithItems);
            setSavedItems(savedRes.data || []);
            setError(null);
        } catch (err) {
            console.error('Error fetching boards or saved items:', err);
            setError(err.response?.data?.message || 'Failed to fetch boards or saved items');
        } finally {
            setLoading(false);
        }
    };

    const handleCreatePin = async (pinData) => {
        if (pinData.board === '__new__') {
            try {
                setLoading(true);
                const token = localStorage.getItem('token');
                if (!token) throw new Error('No authentication token found');
                const response = await axios.post(
                    `${API_URL}/api/boards`,
                    { name: pinData.title, description: pinData.description, items: [pinData] },
                    { headers: { 'x-auth-token': token } }
                );
                setBoards([...boards, { ...response.data, items: [pinData] }]);
                setError(null);
            } catch (err) {
                setError(err.response?.data?.message || 'Failed to create board');
            } finally {
                setLoading(false);
            }
        } else {
            try {
                setLoading(true);
                const token = localStorage.getItem('token');
                if (!token) throw new Error('No authentication token found');
                const boardToUpdate = boards.find(b => b._id === pinData.board || b.name === pinData.board);
                if (!boardToUpdate) throw new Error('Board not found');
                const updatedItems = [...(boardToUpdate.items || []), pinData];
                const response = await axios.put(
                    `${API_URL}/api/boards/${boardToUpdate._id}`,
                    { ...boardToUpdate, items: updatedItems },
                    { headers: { 'x-auth-token': token } }
                );
                setBoards(boards.map(b => b._id === boardToUpdate._id ? { ...response.data } : b));
                setError(null);
            } catch (err) {
                setError(err.response?.data?.message || 'Failed to add pin to board');
            } finally {
                setLoading(false);
            }
        }
    };

    const handleDeleteBoard = async (boardId) => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            if (!token) {
                throw new Error('No authentication token found');
            }

            await axios.delete(`${API_URL}/api/boards/${boardId}`, {
                headers: { 'x-auth-token': token }
            });
            setBoards(boards.filter(board => board._id !== boardId));
            setError(null);
        } catch (err) {
            console.error('Error deleting board:', err);
            setError(err.response?.data?.message || 'Failed to delete board');
        } finally {
            setLoading(false);
        }
    };

    const handleBoardClick = (board) => {
        setSelectedBoard(board);
    };

    // Handler to add image to board
    const handleDropImage = async (boardId, files) => {
        if (!files || files.length === 0) return;
        for (const file of files) {
            const reader = new FileReader();
            reader.onload = async (e) => {
                const base64 = e.target.result;
                // Add image to board items
                try {
                    setLoading(true);
                    const token = localStorage.getItem('token');
                    if (!token) throw new Error('No authentication token found');
                    // Add image item to board
                    const board = boards.find(b => b._id === boardId);
                    const updatedItems = [
                        ...board.items,
                        { type: 'image', image: base64 }
                    ];
                    await axios.put(
                        `${API_URL}/api/boards/${boardId}`,
                        { ...board, items: updatedItems },
                        { headers: { 'x-auth-token': token } }
                    );
                    await fetchBoardsAndSavedItems();
                } catch (err) {
                    setError('Failed to add image');
                } finally {
                    setLoading(false);
                }
            };
            reader.readAsDataURL(file);
        }
    };

    const handleAddImageUrl = async (boardId) => {
        const url = window.prompt('Paste the image URL:');
        if (!url || !/^https?:\/\//i.test(url)) return;
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            if (!token) throw new Error('No authentication token found');
            const board = boards.find(b => b._id === boardId);
            const updatedItems = [
                ...board.items,
                { type: 'image', image: url }
            ];
            await axios.put(
                `${API_URL}/api/boards/${boardId}`,
                { ...board, items: updatedItems },
                { headers: { 'x-auth-token': token } }
            );
            await fetchBoardsAndSavedItems();
        } catch (err) {
            setError('Failed to add image by URL');
        } finally {
            setLoading(false);
        }
    };

    if (!isAuthenticated) {
        return null;
    }

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h4">Inspiration Boards</Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setOpenCreatePin(true)}
                    disabled={loading}
                >
                    Create New Board
                </Button>
            </Box>

            {error && (
                <Snackbar
                    open={!!error}
                    autoHideDuration={6000}
                    onClose={() => setError(null)}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                >
                    <Alert onClose={() => setError(null)} severity="error">
                        {error}
                    </Alert>
                </Snackbar>
            )}

            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                    <CircularProgress />
                </Box>
            )}

            <Grid container spacing={3}>
                {boards.map((board) => {
                    // Get all product items for this board
                    const products = (board.items || [])
                        .filter(item => item.type === 'product')
                        .map(item => savedItems.find(si => si.link === item.ref))
                        .filter(Boolean);
                    // Get all image items for this board
                    const images = (board.items || []).filter(item => item.type === 'image' && item.image);
                    return (
                        <Grid item xs={12} sm={6} md={4} key={board._id}>
                            <Card>
                                {/* Drop zone for images */}
                                <DropZone
                                    isdragover={dragOverBoardId === board._id ? 1 : 0}
                                    onDragOver={e => { e.preventDefault(); setDragOverBoardId(board._id); }}
                                    onDragLeave={e => { e.preventDefault(); setDragOverBoardId(null); }}
                                    onDrop={e => {
                                        e.preventDefault();
                                        setDragOverBoardId(null);
                                        handleDropImage(board._id, e.dataTransfer.files);
                                    }}
                                    onClick={() => {
                                        // Open file picker
                                        const input = document.createElement('input');
                                        input.type = 'file';
                                        input.accept = 'image/*';
                                        input.multiple = true;
                                        input.onchange = (e) => handleDropImage(board._id, e.target.files);
                                        input.click();
                                    }}
                                >
                                    {images.length > 0 ? (
                                        <Box sx={{ display: 'flex', gap: 1, width: '100%', overflowX: 'auto' }}>
                                            {images.map((img, idx) => (
                                                <img
                                                    key={idx}
                                                    src={img.image}
                                                    alt={`Inspiration ${idx + 1}`}
                                                    style={{ maxHeight: 160, maxWidth: 120, borderRadius: 8, objectFit: 'cover', boxShadow: '0 2px 8px #0001' }}
                                                />
                                            ))}
                                        </Box>
                                    ) : (
                                        <Typography color="textSecondary">Drop inspiration photos here or click to upload</Typography>
                                    )}
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2, background: '#fff' }}
                                        onClick={e => { e.stopPropagation(); handleAddImageUrl(board._id); }}
                                    >
                                        Add by URL
                                    </Button>
                                </DropZone>
                                <CardContent>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography variant="h6">{board.name}</Typography>
                                        <IconButton
                                            onClick={() => handleDeleteBoard(board._id)}
                                            color="error"
                                            disabled={loading}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </Box>
                                    <Typography color="textSecondary" gutterBottom>
                                        {board.description}
                                    </Typography>
                                    <Typography variant="body2">
                                        {(board.items || []).length} items
                                    </Typography>
                                    {products.length > 0 && (
                                        <Box sx={{ mt: 2 }}>
                                            {products.map(product => (
                                                <ProductCard
                                                    key={product._id || product.link}
                                                    product={product}
                                                    boardList={boards}
                                                    currentBoardId={board._id}
                                                    onMove={null}
                                                />
                                            ))}
                                        </Box>
                                    )}
                                    <Button
                                        variant="outlined"
                                        fullWidth
                                        sx={{ mt: 2 }}
                                        onClick={() => handleBoardClick(board)}
                                        disabled={loading}
                                    >
                                        Open Board
                                    </Button>
                                </CardContent>
                            </Card>
                        </Grid>
                    );
                })}
            </Grid>

            <CreatePinModal
                open={openCreatePin}
                onClose={() => setOpenCreatePin(false)}
                boards={[...boards, { _id: '__new__', name: 'Create new board...' }]}
                onCreate={handleCreatePin}
            />

            {selectedBoard && (
                <Dialog
                    open={!!selectedBoard}
                    onClose={() => setSelectedBoard(null)}
                    maxWidth="lg"
                    fullWidth
                >
                    <DialogTitle>{selectedBoard.name}</DialogTitle>
                    <DialogContent>
                        <Typography variant="subtitle1" color="textSecondary" sx={{ mb: 2 }}>
                            {selectedBoard.description}
                        </Typography>
                        {(() => {
                            console.log('Dialog: selectedBoard.items', selectedBoard.items);
                            console.log('Dialog: savedItems', savedItems);
                            const products = (selectedBoard.items || [])
                                .filter(item => item.type === 'product')
                                .map(item => savedItems.find(si => si.link === item.ref))
                                .filter(Boolean);
                            if (products.length === 0) {
                                return <Typography color="textSecondary">No products in this board yet.</Typography>;
                            }
                            return (
                                <Box sx={{ mb: 3 }}>
                                    {products.map(product => (
                                        <ProductCard
                                            key={product._id || product.link}
                                            product={product}
                                            boardList={boards}
                                            currentBoardId={selectedBoard._id}
                                            onMove={null}
                                        />
                                    ))}
                                </Box>
                            );
                        })()}
                        {selectedBoard.items && selectedBoard.items.length > 0 ? (
                            <ImageList cols={3} gap={16} sx={{ width: '100%', maxHeight: 500 }}>
                                {selectedBoard.items.map((item, idx) => {
                                    let imgSrc = item.imagePreview;
                                    if (!imgSrc && item.image) {
                                        if (typeof item.image === 'string') {
                                            imgSrc = item.image;
                                        } else if (item.image instanceof File) {
                                            imgSrc = URL.createObjectURL(item.image);
                                        }
                                    }
                                    if (!imgSrc) return null;
                                    return (
                                        <ImageListItem key={idx}>
                                            <img
                                                src={imgSrc}
                                                alt={item.title || `Pin ${idx + 1}`}
                                                style={{ width: '100%', height: 'auto', borderRadius: 8 }}
                                            />
                                            <Typography variant="body2" align="center" sx={{ mt: 1 }}>
                                                {item.title}
                                            </Typography>
                                        </ImageListItem>
                                    );
                                })}
                            </ImageList>
                        ) : (
                            <Typography variant="body2" color="textSecondary">
                                No images in this board yet.
                            </Typography>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setSelectedBoard(null)}>Close</Button>
                    </DialogActions>
                </Dialog>
            )}
        </Box>
    );
};

export default InspirationBoard; 